import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants';

interface GeneratingItem {
  id: string;
  name: string;
  type: 'invitation' | 'presentation' | 'render';
  status: string;
  created: string;
}

const TYPE_LABELS: Record<string, string> = {
  presentation: 'Presentation',
  invitation: 'Invitation',
  render: 'Render',
};

/**
 * Global hook that polls for generating presentations/invitations/renders
 * and fires toast notifications when they finish (completed or failed).
 *
 * Mount once in DashboardLayout so it works on every dashboard page.
 */
export function useGenerationTracker() {
  const queryClient = useQueryClient();
  const prevIdsRef = useRef<Set<string>>(new Set());

  const { data: generating } = useQuery<GeneratingItem[]>({
    queryKey: ['generating-status'],
    queryFn: () => api.get<GeneratingItem[]>(API_ROUTES.generatingStatus).then((r) => r.data),
    refetchInterval: (query) => {
      const items = query.state.data;
      // Poll every 5s while there are generating items, stop otherwise
      return items && items.length > 0 ? 5_000 : false;
    },
    staleTime: 0,
  });

  // Detect transitions: item was in prevIds but is no longer generating
  useEffect(() => {
    if (!generating) return;

    const currentIds = new Set(generating.map((g) => g.id));
    const prev = prevIdsRef.current;

    // Items that were generating but are no longer in the list → finished
    for (const id of prev) {
      if (!currentIds.has(id)) {
        // Fetch the individual item to check final status
        api
          .get<{ id: string; name: string; type: string; status: string }>(
            `${API_ROUTES.presentations}${id}/`,
          )
          .then((res) => {
            const item = res.data;
            const label = TYPE_LABELS[item.type] || 'Item';

            if (item.status === 'completed') {
              toast.success(`${label} "${item.name}" is ready!`, { duration: 6000 });
            } else if (item.status === 'failed') {
              toast.error(`${label} "${item.name}" failed to generate`, { duration: 6000 });
            }

            // Invalidate the relevant list query so it refreshes
            queryClient.invalidateQueries({ queryKey: [`${item.type}s`] });
          })
          .catch(() => {
            // If fetch fails, still invalidate lists
            queryClient.invalidateQueries({ queryKey: ['presentations'] });
            queryClient.invalidateQueries({ queryKey: ['invitations'] });
            queryClient.invalidateQueries({ queryKey: ['renders'] });
          });
      }
    }

    prevIdsRef.current = currentIds;
  }, [generating, queryClient]);

  return { generatingCount: generating?.length ?? 0 };
}
